import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit3, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  Heart,
  ClipboardList,
  FileText,
  AlertCircle,
  Clock,
  Activity,
  ShoppingCart,
  CheckCircle,
  FileClock,
  ExternalLink,
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { PrescriptionOrderWorkflow } from '../components/orders/PrescriptionOrderWorkflow';
import { OrderHistory } from '../components/orders/OrderHistory';
import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import { PatientLabResults } from '../components/labs/PatientLabResults';

interface Patient {
  _id: string;
  physicianId: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  patientType?: 'human' | 'animal';
  animal?: {
    name?: string;
    species?: string;
    breed?: string;
    sex?: string;
    weightLbs?: number;
    ageYears?: number;
  };
  phone: string;
  email?: string;
  address: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  status: 'active' | 'inactive';
  medicalHistory?: string;
  allergies?: string;
  notes?: string;
  screeningAnswers?: {
    biologicalSex?: 'male' | 'female';
    heightInches?: number;
    weightLbs?: number;
    bmi?: number;
    hasCancer?: boolean;
    isPregnantOrBreastfeeding?: boolean;
    hasProstateIssues?: boolean;
    hasThyroidCondition?: boolean;
    takingMedications?: boolean;
    currentMedications?: string;
    isFirstTimePeptides?: boolean;
    hasAllergies?: boolean;
    allergies?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  status: 'draft' | 'pending' | 'awaiting_payment' | 'submitted' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'failed';
  totalAmount: number;
  createdAt: string;
}


const PATIENT_ORDERS_PAGE_SIZE = 50;
export const PatientProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOrderWorkflow, setShowOrderWorkflow] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [orderComplete, setOrderComplete] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersTotal, setOrdersTotal] = useState<number | null>(null);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const [showMobileFAB, setShowMobileFAB] = useState(false);
  // Orders auto-charged via MIT submit to the pharmacy only once the payment
  // webhook lands, so a fresh fetch still reports them as "Awaiting Payment".
  // Track those ids and optimistically render them as "Submitted" until the
  // server catches up. Persists across refetches and becomes a no-op once the
  // real status advances past awaiting_payment.
  const optimisticSubmittedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (id) {
      loadPatient(id);
      loadPatientOrders(id);
    }
  }, [id]);

  const loadPatient = async (patientId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.getPatient(patientId);
      
      if (response.success) {
        setPatient(response.data);
      } else {
        setError(response.error || 'Failed to load patient');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load patient');
    } finally {
      setLoading(false);
    }
  };

  // Pages of this patient's orders, newest first. A refresh reloads the first
  // page; "Load more" appends the next one, so no order is out of reach.
  const loadPatientOrders = async (patientId: string, append = false) => {
    try {
      if (append) setOrdersLoadingMore(true); else setOrdersLoading(true);
      setOrdersError(null);

      const response = await apiClient.getOrders({
        patientId,
        limit: PATIENT_ORDERS_PAGE_SIZE,
        skip: append ? orders.length : 0,
      });

      if (response.success && response.data) {
        const optimisticIds = optimisticSubmittedRef.current;
        const pageOrders: Order[] = optimisticIds.size === 0
          ? response.data.orders
          : response.data.orders.map((o: Order) =>
              optimisticIds.has(o._id) && o.status === 'awaiting_payment'
                ? { ...o, status: 'submitted' }
                : o
            );
        setOrders(prev => append ? [...prev, ...pageOrders] : pageOrders);
        setOrdersTotal(typeof response.data.pagination?.total === 'number' ? response.data.pagination.total : null);
      } else {
        setOrdersError(response.error || 'Failed to load orders');
      }
    } catch (err: any) {
      setOrdersError(err.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
      setOrdersLoadingMore(false);
    }
  };

  const handleOrderForPatient = () => {
    if (!patient) return;
    setShowOrderWorkflow(true);
  };

  /**
   * Open this patient's own portal in a new tab.
   *
   * The link is single-use and expires in a minute, so it is fetched on click
   * rather than rendered into the page — a link sitting in the DOM would be a
   * live session waiting in anyone's view-source.
   */
  const handleOpenPortal = async () => {
    if (!patient || openingPortal) return;
    setOpeningPortal(true);
    setPortalError(null);
    try {
      const result = await apiClient.impersonatePatient(patient._id);
      if (result.success && result.data?.url) {
        window.open(result.data.url, '_blank', 'noopener,noreferrer');
      } else {
        setPortalError(result.error || 'Could not open the patient portal');
      }
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleOrderComplete = (orderId: string, opts?: { optimisticSubmitted?: boolean }) => {
    setOrderComplete(orderId);
    setShowOrderWorkflow(false);

    // Optimistic update: the MIT auto-charge succeeded but the pharmacy submit
    // is deferred to the payment webhook. Trust the webhook and show the order
    // as submitted now rather than leaving it on "Awaiting Payment".
    // loadPatientOrders applies the override; the next webhook-confirmed fetch
    // reconciles it once the real status advances.
    if (opts?.optimisticSubmitted) {
      optimisticSubmittedRef.current.add(orderId);
    }

    // Show success message for 5 seconds
    setTimeout(() => {
      setOrderComplete(null);
    }, 5000);
    // Refresh orders
    if (patient) {
      loadPatientOrders(patient._id);
    }
  };

  const handleCloseOrderWorkflow = () => {
    setShowOrderWorkflow(false);
  };

  const handleViewOrderHistory = () => {
    // Scroll to the order history section
    const orderHistoryElement = document.getElementById('order-history');
    if (orderHistoryElement) {
      orderHistoryElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const formatDate = (dateString: string) => formatDateUtil(dateString, { month: 'long' });

  // Format date-only fields (like birthdays) as UTC to avoid timezone shift
  const formatDateUTC = (dateString: string) => {
    const date = new Date(dateString);
    // Add timezone offset to get the correct UTC date displayed
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return formatDateUtil(utcDate, { month: 'long' });
  };

  const formatDateTime = (dateString: string) => formatDateTimeUtil(dateString);

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    // Use UTC methods to avoid timezone issues with date-only fields
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthDate.getUTCMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
      age--;
    }
    
    return age;
  };

  if (!user || !['physician', 'physician_readonly'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only physicians can access patient records.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading patient information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/patients')}
            className="text-blue-600 hover:text-blue-800"
          >
            Return to Patient List
          </button>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Patient Not Found</h1>
          <p className="text-gray-600 mb-4">The patient you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/patients')}
            className="text-blue-600 hover:text-blue-800"
          >
            Return to Patient List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Order Success Message */}
      {orderComplete && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <div>
              <p className="text-green-800 font-medium">Order Created Successfully!</p>
              <p className="text-green-600 text-sm">Order ID: {orderComplete}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/patients')}
                className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div data-testid="mobile-patient-header">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {patient.firstName} {patient.lastName}
                  </h1>
                  <p className="text-sm text-gray-600 hidden sm:block">
                    {patient.patientType === 'animal'
                      ? [patient.animal?.species, patient.animal?.name && `“${patient.animal.name}”`].filter(Boolean).join(' ')
                      : patient.dateOfBirth ? `${calculateAge(patient.dateOfBirth)} years old` : ''}
                    {' • '}Patient ID: {patient._id.slice(-6)}
                  </p>
                  {/* Compact mobile version */}
                  <p className="text-sm text-gray-600 block sm:hidden">
                    ID: {patient._id.slice(-6)}
                  </p>
                  {/* Critical info on mobile */}
                  <p className="text-xs text-gray-600 block sm:hidden">
                    Allergies: {patient.allergies || 'None'}
                  </p>
                </div>
              </div>
            </div>
            
            {user.role === 'physician' && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleOrderForPatient}
                  className="inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-lg sm:text-sm font-semibold sm:font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-h-[44px] w-full sm:w-auto"
                >
                  <ShoppingCart className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                  Order for this Patient
                </button>
                <button
                  onClick={() => navigate(`/patients/${patient._id}/edit`)}
                  className="inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-lg sm:text-sm font-semibold sm:font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-[44px] w-full sm:w-auto"
                >
                  <Edit3 className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                  Edit Patient
                </button>
                <button
                  onClick={handleOpenPortal}
                  disabled={openingPortal}
                  title="See this patient's own portal as they see it. Purchases and membership changes stay theirs."
                  className="inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-300 text-lg sm:text-sm font-semibold sm:font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 min-h-[44px] w-full sm:w-auto"
                >
                  <ExternalLink className="h-5 w-5 sm:h-4 sm:w-4 mr-2" />
                  {openingPortal ? 'Opening...' : 'View Patient Portal'}
                </button>
                {portalError && (
                  <p className="text-sm text-red-600 sm:self-center" role="alert">{portalError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: Patient Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Personal / Owner Information */}
            <InfoCard title={patient.patientType === 'animal' ? 'Owner Information' : 'Personal Information'} icon={User}>
              <InfoGrid>
                <InfoItem label={patient.patientType === 'animal' ? 'Owner Name' : 'Full Name'} value={`${patient.firstName} ${patient.lastName}`} />
                {patient.dateOfBirth && (
                  <InfoItem label={patient.patientType === 'animal' ? 'Owner Date of Birth' : 'Date of Birth'} value={formatDateUTC(patient.dateOfBirth)} icon={Calendar} />
                )}
                <InfoItem label="Phone Number" value={patient.phone} icon={Phone} />
                <InfoItem label="Email Address" value={patient.email || 'N/A'} icon={Mail} />
              </InfoGrid>
            </InfoCard>

            {/* Pet Details (vet clinics) */}
            {patient.patientType === 'animal' && (
              <InfoCard title="Pet Details" icon={User}>
                <InfoGrid>
                  <InfoItem label="Pet Name" value={patient.animal?.name || 'N/A'} />
                  <InfoItem label="Species" value={patient.animal?.species || 'N/A'} />
                  <InfoItem label="Weight" value={patient.animal?.weightLbs != null ? `${patient.animal.weightLbs} lb` : 'N/A'} />
                  <InfoItem label="Breed" value={patient.animal?.breed || 'N/A'} />
                  <InfoItem label="Sex" value={patient.animal?.sex || 'N/A'} />
                  <InfoItem label="Age" value={patient.animal?.ageYears != null ? `${patient.animal.ageYears} yr` : 'N/A'} />
                </InfoGrid>
              </InfoCard>
            )}

            {/* Address Information */}
            <InfoCard title="Address Information" icon={MapPin}>
              <InfoGrid>
                <InfoItem label="Street Address" value={patient.address.street} />
                {patient.address.street2 && (
                  <InfoItem label="Address Line 2" value={patient.address.street2} />
                )}
                <InfoItem label="City" value={patient.address.city} />
                <InfoItem label="State" value={patient.address.state} />
                <InfoItem label="ZIP Code" value={patient.address.zipCode} />
              </InfoGrid>
            </InfoCard>

            {/* Medical Information */}
            <InfoCard title="Medical Information" icon={Heart}>
              <div className="space-y-4">
                <p className="text-sm text-gray-600"><strong className="font-medium text-gray-800">Medical History:</strong> {patient.medicalHistory || 'No history provided.'}</p>
                <p className="text-sm text-gray-600"><strong className="font-medium text-gray-800">Allergies:</strong> {patient.allergies || 'No allergies recorded.'}</p>
                <p className="text-sm text-gray-600"><strong className="font-medium text-gray-800">Physician Notes:</strong> {patient.notes || 'No notes available.'}</p>
              </div>
            </InfoCard>

            {/* Health Screening */}
            {patient.screeningAnswers && Object.keys(patient.screeningAnswers).length > 0 && (
              <InfoCard title="Health Screening" icon={ClipboardList}>
                <InfoGrid>
                  {patient.screeningAnswers.biologicalSex && (
                    <InfoItem label="Biological Sex" value={patient.screeningAnswers.biologicalSex === 'male' ? 'Male' : 'Female'} />
                  )}
                  {patient.screeningAnswers.heightInches != null && (
                    <InfoItem label="Height" value={`${Math.floor(patient.screeningAnswers.heightInches / 12)}'${patient.screeningAnswers.heightInches % 12}"`} />
                  )}
                  {patient.screeningAnswers.weightLbs != null && (
                    <InfoItem label="Weight" value={`${patient.screeningAnswers.weightLbs} lbs`} />
                  )}
                  {patient.screeningAnswers.bmi != null && (
                    <InfoItem label="BMI" value={patient.screeningAnswers.bmi.toFixed(1)} />
                  )}
                </InfoGrid>
                <div className="mt-4 divide-y divide-gray-100">
                  <ScreeningRow label="Active cancer diagnosis" value={patient.screeningAnswers.hasCancer} />
                  {patient.screeningAnswers.biologicalSex === 'female' && (
                    <ScreeningRow label="Pregnant or breastfeeding" value={patient.screeningAnswers.isPregnantOrBreastfeeding} />
                  )}
                  {patient.screeningAnswers.biologicalSex === 'male' && (
                    <ScreeningRow label="Prostate issues" value={patient.screeningAnswers.hasProstateIssues} />
                  )}
                  <ScreeningRow label="Thyroid condition" value={patient.screeningAnswers.hasThyroidCondition} />
                  <ScreeningRow label="Currently taking medications" value={patient.screeningAnswers.takingMedications} />
                  {patient.screeningAnswers.takingMedications && patient.screeningAnswers.currentMedications && (
                    <div className="py-2 pl-4">
                      <p className="text-sm text-gray-600">{patient.screeningAnswers.currentMedications}</p>
                    </div>
                  )}
                  <ScreeningRow label="First time taking peptides" value={patient.screeningAnswers.isFirstTimePeptides} />
                  <ScreeningRow label="Known allergies" value={patient.screeningAnswers.hasAllergies} />
                  {patient.screeningAnswers.hasAllergies && patient.screeningAnswers.allergies && (
                    <div className="py-2 pl-4">
                      <p className="text-sm text-gray-600">{patient.screeningAnswers.allergies}</p>
                    </div>
                  )}
                </div>
              </InfoCard>
            )}
          </div>

          {/* Right Column: Quick Actions & Patient Info */}
          <div className="space-y-8">
            {/* Quick Actions - Hidden on mobile, shown on desktop */}
            <div className="hidden sm:block">
              <InfoCard title="Quick Actions" icon={Activity}>
                <div className="space-y-3">
                  <QuickActionButton
                    label="Edit Patient Information"
                    onClick={() => navigate(`/patients/${patient._id}/edit`)}
                    icon={Edit3}
                    className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                  />
                  <QuickActionButton
                    label="Create New Order"
                    onClick={handleOrderForPatient}
                    icon={ShoppingCart}
                    className="bg-green-50 text-green-700 hover:bg-green-100"
                  />
                  <QuickActionButton
                    label="View Order History"
                    onClick={handleViewOrderHistory}
                    icon={FileClock}
                    className="bg-gray-50 text-gray-700 hover:bg-gray-100"
                  />
                </div>
              </InfoCard>
            </div>

            {/* Patient Information Summary */}
            <InfoCard title="Patient Information" icon={FileText}>
               <InfoGrid>
                {patient.patientType === 'animal' ? (
                  <InfoItem label="Pet" value={[patient.animal?.species, patient.animal?.name].filter(Boolean).join(' • ') || 'N/A'} />
                ) : (
                  <InfoItem label="Age" value={patient.dateOfBirth ? `${calculateAge(patient.dateOfBirth)} years old` : 'N/A'} />
                )}
                <InfoItem label="Patient Since" value={formatDate(patient.createdAt)} />
                <InfoItem label="Last Updated" value={formatDateTime(patient.updatedAt)} />
               </InfoGrid>
            </InfoCard>
          </div>
        </div>

        {/* Lab results. Renders nothing when the patient has none, which is
            most of them — see PatientLabResults. */}
        <div className="mt-8">
          <PatientLabResults patientId={patient._id} />
        </div>

        {/* Full Width Order History Section */}
        <div className="mt-8">
          <InfoCard title="Order History" icon={Clock} id="order-history">
            {ordersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <Activity className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-4" />
                  <p className="text-gray-600">Loading order history...</p>
                </div>
              </div>
            ) : ordersError ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                <p className="text-red-500">{ordersError}</p>
              </div>
            ) : (
              <>
                <OrderHistory
                  orders={orders}
                  onSelectOrder={(orderId) => navigate(`/orders/${orderId}`)}
                />
                {ordersTotal !== null && ordersTotal > orders.length && (
                  <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-gray-100 text-sm text-gray-600">
                    <span>Showing {orders.length} of {ordersTotal} orders</span>
                    <button
                      type="button"
                      onClick={() => loadPatientOrders(patient._id, true)}
                      disabled={ordersLoadingMore}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {ordersLoadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </InfoCard>
        </div>
      </main>

      {/* Mobile Floating Action Button - Quick Actions */}
      {user.role === 'physician' && (
        <div 
          className="fixed bottom-4 right-4 z-40 block sm:hidden"
          data-testid="mobile-actions-fab"
        >
          <div className="relative group">
            <button
              onClick={() => setShowMobileFAB(!showMobileFAB)}
              className="w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Activity className="h-6 w-6" />
            </button>
            
            {/* FAB Menu */}
            {showMobileFAB && (
              <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border p-2 min-w-[200px]">
                <div className="space-y-1">
                  <button
                    onClick={() => {
                      handleOrderForPatient();
                      setShowMobileFAB(false);
                    }}
                    className="w-full flex items-center text-left p-3 rounded-md hover:bg-green-50 text-green-700 transition-colors"
                  >
                    <ShoppingCart className="h-5 w-5 mr-3" />
                    <span className="font-medium">Create New Order</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate(`/patients/${patient._id}/edit`);
                      setShowMobileFAB(false);
                    }}
                    className="w-full flex items-center text-left p-3 rounded-md hover:bg-blue-50 text-blue-700 transition-colors"
                  >
                    <Edit3 className="h-5 w-5 mr-3" />
                    <span className="font-medium">Edit Patient Information</span>
                  </button>
                  <button
                    onClick={() => {
                      handleViewOrderHistory();
                      setShowMobileFAB(false);
                    }}
                    className="w-full flex items-center text-left p-3 rounded-md hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <FileClock className="h-5 w-5 mr-3" />
                    <span className="font-medium">View Order History</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Workflow Modal */}
      {showOrderWorkflow && (
        <PrescriptionOrderWorkflow
          isOpen={showOrderWorkflow}
          onClose={handleCloseOrderWorkflow}
          patientId={patient._id}
          patientName={`${patient.firstName} ${patient.lastName}`}
          onOrderComplete={handleOrderComplete}
        />
      )}
    </div>
  );
};

// Helper Components for layout

interface InfoCardProps {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  id?: string;
}

const InfoCard: React.FC<InfoCardProps> = ({ title, icon: Icon, children, id }) => (
  <div className="bg-white rounded-lg shadow" id={id}>
    <div className="flex items-center p-4 border-b border-gray-200">
      <Icon className="h-6 w-6 text-gray-500 mr-3" />
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const InfoGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
    {children}
  </dl>
);

interface InfoItemProps {
  label: string;
  value: string;
  icon?: React.ElementType;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, icon: Icon }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 flex items-center">
      {Icon && <Icon className="h-4 w-4 text-gray-400 mr-2" />}
      <span>{value}</span>
    </dd>
  </div>
);

interface QuickActionButtonProps {
  label: string;
  onClick: () => void;
  icon: React.ElementType;
  className: string;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ label, onClick, icon: Icon, className }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center text-left p-3 rounded-lg transition-colors ${className}`}
  >
    <Icon className="h-5 w-5 mr-3" />
    <span className="font-medium">{label}</span>
  </button>
);

const ScreeningRow: React.FC<{ label: string; value?: boolean | null }> = ({ label, value }) => {
  if (value == null) return null;
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm text-gray-700">{label}</span>
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        value
          ? 'bg-red-50 text-red-700'
          : 'bg-green-50 text-green-700'
      }`}>
        {value ? 'Yes' : 'No'}
      </span>
    </div>
  );
};